import React, { useEffect, useState, useMemo } from 'react';
import styles from './ApprovalSection.module.scss';
import Icon from '../../../common/Icon/Icon';
import { useAppSelector } from '../../../../redux/hooks';
import { RootState } from '../../../../redux/store';
import FormatterBar from '../../../common/FormatterBar/FormatterBar';
import HTMLtoText from '../../../common/HTMLToText/HTMLToText';
import { useTranslation } from 'react-i18next';
import Button from '../../../common/Button/Button';
import { trackingAwardApprovalSection } from '../../../../types/types';
import { AWARD_APPROVAL_STATUS_APPROVER } from '../../../../constants/dataConstants';
import Spinner from '../../../common/Spinner/Spinner';
import ErrorHandlingComponent from '../../../../components/ErrorHandling/ErrorHandling';
import { useLazyGetUserNameWithIDQuery } from '../../../../redux/services/hatvAboStatisticAPI';
import Modal from '../../../common/Modal/Modal';
import HATVApprovalActionModal, {
  IApproveModalProps,
} from '../../HATVApprovalAction/HATVApprovalActionModal';
import { useProfileParams } from '../../../../utils/common';
import ViewRoutingModalWrapper from '../../ViewRoutingModal/ViewRoutingModal';

interface Props {
  tabs?: trackingAwardApprovalSection[];
  onTabChange: (id: string) => void;
  selectedTabId?: string | number | undefined;
  approvalStatus?: string | number;
  assignedApprover?: string;
  nextApprover?: string;
  caseEvaluator?: string;
  hatvRoutingRoleIndicators?: any;
  hatvApprovalRoleIndicators?: any;
  HATVSummaryData?: any;
  HATVToDoData?: any;
  handleSummaryEdit: (note: string) => void;
  handleTODOEdit: (note: string) => void;
  isToDoLoading: boolean;
  isSummaryLoading: boolean;
  selectedTabDetailsData: any;
  setIsStatusUpdate?: (apiStatus: string) => void;
}

const ApprovalSection: React.FC<Props> = ({
  tabs,
  onTabChange,
  selectedTabId,
  approvalStatus,
  assignedApprover,
  nextApprover,
  caseEvaluator,
  HATVSummaryData,
  HATVToDoData,
  hatvRoutingRoleIndicators,
  hatvApprovalRoleIndicators,
  handleSummaryEdit,
  handleTODOEdit,
  isToDoLoading,
  isSummaryLoading,
  selectedTabDetailsData,
  setIsStatusUpdate,
}) => {
  const [selectedAction, setSelectedAction] = useState(false);
  const [summaryEditData, setSummaryEditData] = useState('');
  const [toDoEditData, setToDoEditData] = useState('');
  const [assignedApproverName, setAssignedApprover] = useState('');
  const [nextApproverName, setNextApproverName] = useState('');
  const [caseEvaluatorName, setCaseEvaluatorName] = useState('');
  const userNativeId = localStorage.getItem('auth-nativeId');
  const [selectedTabData, setSelectedTabData] = useState<trackingAwardApprovalSection>();

  const currentTheme = useAppSelector((state: RootState) => state.theme.currentTheme);
  const { t } = useTranslation();
  const iconColor = !selectedAction
    ? currentTheme === 'light'
      ? '#2c2c2c'
      : '#D8D8D9'
    : currentTheme === 'light'
      ? '#5E5E5E'
      : '#000';

  const ViewApprovalModalStyles = {
    minWidth: '472px',
    minHeight: '400px',
    overflow: 'visible',
  };

  const [approvalModalState, setApprovalModalState] = useState<Omit<
    IApproveModalProps,
    'onClose'
  > | null>(null);
  const { affiliateCode, aboNumber } = useProfileParams();
  const [isAwardApproval, setIsAwardApproval] = useState(false);

  const [isEditable, setIsEditable] = useState(false);
  const [isToDoEditable, setIsToDoEditable] = useState(false);

  const handleClickEditSummary = () => {
    setIsEditable(true);
  };
  const handleClickEditToDo = () => {
    setIsToDoEditable(true);
  };

  const handleClickCancel = () => {
    setIsEditable(false);
    setIsToDoEditable(false);
  };

  const matchedApprover = selectedTabDetailsData?.awardDetail?.awardApproval?.awardApprovers?.find(
    (approver: any) => approver?.userNativeId === userNativeId
  );
  const handleSummaryDataChange = (notes: any) => {
    setSummaryEditData(notes);
  };
  const handleToDoDataChange = (notes: any) => {
    setToDoEditData(notes);
  };

  const handleClickSummarySave = async () => {
    setIsEditable(false);
    setIsToDoEditable(false);
    handleSummaryEdit(summaryEditData);
  };

  const handleTODOClickSave = async () => {
    setIsEditable(false);
    setIsToDoEditable(false);
    handleTODOEdit(toDoEditData);
  };

  const [getUserNameFromIdAA] = useLazyGetUserNameWithIDQuery();
  const [getUserNameFromIdNA] = useLazyGetUserNameWithIDQuery();
  const [getUserNameFromIdCE] = useLazyGetUserNameWithIDQuery();

  const getAssignedApproverName = () => {
    if (assignedApprover)
      getUserNameFromIdAA({ nativeId: assignedApprover }, true).then((respAA) => {
        setAssignedApprover(respAA?.data?.body?.fullName || assignedApprover);
      });
    else setAssignedApprover('');
  };

  const getNextApproverName = () => {
    if (nextApprover)
      getUserNameFromIdNA({ nativeId: nextApprover }, true).then((respNA) => {
        setNextApproverName(respNA?.data?.body?.fullName || nextApprover);
      });
    else setNextApproverName('');
  };

  const getCaseEvaluatorName = () => {
    if (caseEvaluator)
      getUserNameFromIdCE({ nativeId: caseEvaluator }, true).then((respCE) => {
        setCaseEvaluatorName(respCE?.data?.body?.fullName || caseEvaluator);
      });
    else setCaseEvaluatorName('');
  };

  useEffect(() => {
    getAssignedApproverName();
    getNextApproverName();
    getCaseEvaluatorName();
  }, []);

  const approveHandler = () => {
    setIsAwardApproval(true);
    setApprovalModalState({
      aboNumber: aboNumber.toString(),
      affiliateCode,
      approvedFor: localStorage.getItem('subHeaderName') || '',
      aboHatvAwardApproverIdToApprove: matchedApprover?.aboHatvAwardApproverId,
      aboHatvAwardApproverIdToDeny: matchedApprover?.aboHatvAwardApproverId,
      approvalType: 3,
      currentStatus: (approvalStatus && +approvalStatus) || 0,
      awardQualifiedMonth: selectedTabData?.awardQualifiedMonth || '',
    });
  };

  const denyHandler = () => {
    setIsAwardApproval(true);
    setApprovalModalState({
      aboNumber: aboNumber.toString(),
      affiliateCode,
      approvedFor: localStorage.getItem('subHeaderName') || '',
      aboHatvAwardApproverIdToApprove: matchedApprover?.aboHatvAwardApproverId,
      aboHatvAwardApproverIdToDeny: matchedApprover?.aboHatvAwardApproverId,
      approvalType: 9,
      currentStatus: (approvalStatus && +approvalStatus) || 0,
      awardQualifiedMonth: selectedTabData?.awardQualifiedMonth || '',
    });
  };

  const onModalClose = () => {
    setIsAwardApproval(false);
    setApprovalModalState(null);
  };

  const handleQualificationModalAction = (status: string) => {
    setIsStatusUpdate && setIsStatusUpdate(status);
    setIsAwardApproval(false);
    setApprovalModalState(null);
  };

  useEffect(() => {
    if (selectedTabId && tabs) {
      setSelectedTabData(
        tabs?.find(
          (selectedTab) =>
            selectedTab?.awardCode + '#' + selectedTab?.awardQualifiedMonth === selectedTabId
        )
      );
    }
  }, [selectedTabId, tabs]);

  const approverStatus = AWARD_APPROVAL_STATUS_APPROVER.find(
    (statusCode) => statusCode?.code === Number(approvalStatus)
  )
    ? AWARD_APPROVAL_STATUS_APPROVER.find(
        (statusCode) => statusCode?.code === Number(approvalStatus)
      )?.label
    : 'NOT_VERIFIED';

  const selectedAboData = useMemo(() => {
    if (!selectedTabData) return [];
    return [
      {
        name: localStorage.getItem('subHeaderName') || '',
        affiliateCode: affiliateCode,
        aboNumber: String(aboNumber),
        currentAward: {
          awardCode: String(selectedTabData.awardCode),
          awardName: String(selectedTabData.awardName),
        },
        trackingAward: {
          awardCode: String(selectedTabData.awardCode),
          awardName: String(selectedTabData.awardName),
          awardTagCode: Number(selectedTabData.awardTagCode),
          awardTagName: selectedTabData.awardTagName || '',
          awardQualifiedMonth: String(selectedTabData.awardQualifiedMonth),
          awardQualifiedYear: String(selectedTabData.performanceYear || ''),
        },
        routingRoleIndicators: hatvRoutingRoleIndicators,
        uplineDiamond: '',
        uplinePlatinum: '',
        manager: '',
        status: 0,
        statusName: '',
        kpis: [],
      } as TableRowData,
    ];
  }, [selectedTabData, affiliateCode, aboNumber, hatvRoutingRoleIndicators]);

  return (
    <div className={`award_approval_status ${styles.award_approval_status}`}>
      <div className={styles.header_top}>
        <div className={`tab_container ${styles.tab_container}`}>
          {tabs?.map((tab, index) => (
            <span
              key={index}
              className={`tab_item ${styles.tab_item} ${selectedTabId === tab?.awardCode + '#' + tab?.awardQualifiedMonth ? 'selected' : ''}`}
              onClick={() => onTabChange(tab?.awardCode + '#' + tab?.awardQualifiedMonth)}
            >
              {`${tab?.awardName} - ${tab?.awardTagName}`}
            </span>
          ))}
        </div>
      </div>
      <div className={styles.status_content_wrapper}>
        <div className={styles.status_wrapper}>
          <div className={styles.status_section}>
            <span className={`status_label ${styles.status_label}`}>
              {t('ASSIGNED_APPROVAL_STATUS')}
            </span>
            <span
              className={`status_badge ${styles.status_badge} ${approvalStatus === 'Verified' ? 'verified' : 'unverified'}`}
            >
              {t(`HATV_APPROVAL_STATUS.${approverStatus}`)}
            </span>
          </div>
          <div className={`sub_header ${styles.sub_header}`}>
            <span className={`approver_name ${styles.approver_name}`}>
              {t('ASSIGNED_APPROVER')} {assignedApproverName || assignedApprover || '-'}
            </span>
            <span>
              {t('NEXT_APPROVER')} {nextApproverName || nextApprover || '-'}
            </span>
          </div>
          <div className={`sub_header ${styles.sub_header_case}`}>
            <span className="section_sub_heading">
              {(t('CASE_EVALUATOR') || '')?.toUpperCase()}
            </span>
            <span className={`case_text ${styles.caseLabel}`}>
              {caseEvaluatorName || caseEvaluator || '-'}
            </span>
          </div>
        </div>

        <div className={`status_action_wrapper ${styles.status_action_wrapper}`}>
          {hatvApprovalRoleIndicators && hatvApprovalRoleIndicators?.isAllowToApprove && (
            <div className={styles.action_btn}>
              <Button
                id="approvalbtn"
                onClick={approveHandler}
                label={t('Approve')}
                style="unfilled"
                isIcon={true}
                iconName="tick_icon"
                iconSize={16}
                themeColor={iconColor}
                className={`btn ${styles.btn}`}
              />
            </div>
          )}

          {hatvApprovalRoleIndicators && hatvApprovalRoleIndicators?.isAllowToDeny && (
            <div className={styles.action_btn}>
              <Button
                id="denybtn"
                onClick={denyHandler}
                label={t('Deny')}
                style="unfilled"
                isIcon={true}
                iconName="cross_circle_icon"
                iconSize={16}
                themeColor={iconColor}
                className={`btn ${styles.btn}`}
              />
            </div>
          )}
          {hatvRoutingRoleIndicators &&
            hatvRoutingRoleIndicators?.isAuthorizedViewRouting &&
            selectedTabData && (
              <div className={styles.action_btn}>
                <ViewRoutingModalWrapper
                  selectedAbos={selectedAboData}
                  performanceYear={String(selectedTabData.performanceYear || '')}
                  setIsStatusUpdate={handleQualificationModalAction}
                >
                  <Button
                    id="routingbtn"
                    onClick={() => {}}
                    label={t('VIEW_ROUTING')}
                    style="unfilled"
                    isIcon={true}
                    iconName="view_routing_icon"
                    iconSize={16}
                    themeColor={iconColor}
                    className={`btn ${styles.btn}`}
                  />
                </ViewRoutingModalWrapper>
              </div>
            )}
        </div>
      </div>
      <div className={`status_data_container ${styles.status_data_container}`}>
        <div className={styles.common_wrapper}>
          <div className={styles.title_wrapper}>
            <div className={styles.title}>{t('SUMMARY')}</div>
            {!isEditable && !isSummaryLoading && (
              <div className={styles.edit_action}>
                <span
                  className={styles.edit_btn}
                  onClick={handleClickEditSummary}
                  data-testid="edit_testID"
                >
                  <span
                    className={
                      currentTheme === 'light' ? styles.edit_icon_light : styles.edit_icon_dark
                    }
                  ></span>
                  <span className={styles.edit_text}>{t('edit')}</span>
                </span>
              </div>
            )}
          </div>

          <div className={`item_wrapper ${styles.item_wrapper}`}>
            {isSummaryLoading ? (
              <span className={styles.spinner}>
                <Spinner />
              </span>
            ) : HATVSummaryData?.isAPIError ? (
              <ErrorHandlingComponent error={'genericError'} />
            ) : isEditable ? (
              <FormatterBar
                id="summary"
                // label={"Summary"}
                editorData={HATVSummaryData?.note || ''}
                handleDataChange={handleSummaryDataChange}
                section={'how_build_business'}
                isContentEditable={isEditable}
                maxCharacter={2000}
              />
            ) : (
              <div className={styles.summary_textarea}>
                <div className={`grey_text ${styles.item_value}`}>
                  <HTMLtoText
                    htmlContent={HATVSummaryData?.note || ''}
                    alternateMessage={t('no_info_available')}
                  />
                </div>
              </div>
            )}
          </div>
          {isEditable && (
            <div className={styles.btn_wrapper}>
              <Button
                id="cancelButton"
                onClick={handleClickCancel}
                label={t('btnCancel')}
                style="unfilled"
                className={styles.auto_btn}
              />
              <Button
                id="saveButton"
                onClick={handleClickSummarySave}
                label={t('btnSave')}
                style="filled"
                className={styles.auto_btn}
              />
            </div>
          )}
        </div>
        <div className={styles.common_wrapper}>
          <div className={styles.title_wrapper}>
            <div className={styles.title}>{t('TO_DO')}</div>
            {!isToDoEditable && !isToDoLoading && (
              <div className={styles.edit_action}>
                <span
                  className={styles.edit_btn}
                  onClick={handleClickEditToDo}
                  data-testid="edit_testID"
                >
                  <span
                    className={
                      currentTheme === 'light' ? styles.edit_icon_light : styles.edit_icon_dark
                    }
                  ></span>
                  <span className={styles.edit_text}>{t('edit')}</span>
                </span>
              </div>
            )}
          </div>
          <div className={`item_wrapper ${styles.item_wrapper}`}>
            {isToDoLoading ? (
              <span className={styles.spinner}>
                <Spinner />
              </span>
            ) : HATVSummaryData?.isAPIError ? (
              <ErrorHandlingComponent error={'genericError'} />
            ) : isToDoEditable ? (
              <FormatterBar
                id="todo"
                // label={"TO DO"}
                editorData={HATVToDoData?.note || ''}
                handleDataChange={handleToDoDataChange}
                section={'how_build_business'}
                isContentEditable={isToDoEditable}
                maxCharacter={2000}
              />
            ) : (
              <div className={styles.summary_textarea}>
                <div className={`grey_text ${styles.item_value}`}>
                  <HTMLtoText
                    htmlContent={HATVToDoData?.note || ''}
                    alternateMessage={t('no_info_available')}
                  />
                </div>
              </div>
            )}
          </div>
          {isToDoEditable && (
            <div className={styles.btn_wrapper}>
              <Button
                id="cancelButton"
                onClick={handleClickCancel}
                label={t('btnCancel')}
                style="unfilled"
                className={styles.auto_btn}
              />
              <Button
                id="saveButton"
                onClick={handleTODOClickSave}
                label={t('btnSave')}
                style="filled"
                className={styles.auto_btn}
              />
            </div>
          )}

          {isAwardApproval && (
            <Modal
              showDrawer={!!approvalModalState}
              onClose={onModalClose}
              customStyle={ViewApprovalModalStyles}
            >
              {approvalModalState && (
                <HATVApprovalActionModal
                  {...approvalModalState}
                  onClose={onModalClose}
                  setIsStatusUpdate={handleQualificationModalAction}
                />
              )}
            </Modal>
          )}
        </div>
      </div>
      <div></div>
    </div>
  );
};

export default ApprovalSection;
